// Libs
import React, { ReactElement } from 'react';
import clsx from 'clsx';
import { MenuList, MenuItem, Icon, Stack, ListItemIcon, Tooltip } from 'ui-components';
import { usePathname } from 'next/navigation';
import { useSession } from 'next-auth/react';
// App
import styles from './nav-menu.module.scss';
import { SHOP_ADMIN_PAGES_URLS } from '@/fragments/shop-administration/shop-administration.constants';
import { UnstyledLink } from '@/components/navigation';
import { isRouterPathMatchingRoute } from 'app-shared/lib/utils';
import { useGetShop } from 'app-shared/lib/api/shop-administration/shop';
import { hasAuthority } from 'app-shared/lib/api/user-access/auth/auth.utils';
import { AUTHORITY, useGetAccount } from 'app-shared/lib/api/user-access';
import { NavMenuProps } from './nav-menu-props.interface';
import PendingCountBadge from './pending-count-badge';
import { usePendingOrdersCount, usePendingReservationsCount } from './pending-counts';
import StatsSvg from '../../_shared/icons/stats.svg';
import BarsSortSvg from '../../_shared/icons/bars-sort.svg';
import FileInvoiceDollarSvg from '../../_shared/icons/file-invoice-dollar.svg';
import TargetAudienceSvg from '../../_shared/icons/target-audience.svg';
import BrowserSvg from '../../_shared/icons/browser.svg';
import AppsSvg from '../../_shared/icons/apps.svg';
import GearsSvg from '../../_shared/icons/gears.svg';
import SettingsSvg from '../../_shared/icons/settings.svg';
import SettingsSlidersSvg from '../../_shared/icons/settings-sliders.svg';
import TicketSvg from '../../_shared/icons/ticket.svg';
import CalendarCheckSvg from '../../_shared/icons/calendar-check.svg';

const NavMenu = (props: NavMenuProps): ReactElement => {
  const { className, isCollapsed, ...rest } = props;
  const pathname = usePathname();
  const { data: session } = useSession();
  const isUserShopAdmin = session && hasAuthority(session, AUTHORITY.SHOP_ADMIN);
  const isUserOrderAdmin = session && hasAuthority(session, AUTHORITY.SHOP_ORDER_ADMIN);
  const isUserReservationAdmin = session && hasAuthority(session, AUTHORITY.SHOP_RESERVATION_ADMIN);
  const { data: account } = useGetAccount(isUserShopAdmin);
  const shopId = account && account.shopId;
  const { data: shop } = useGetShop(shopId);
  const pendingOrdersCount = usePendingOrdersCount(shopId, Boolean(isUserOrderAdmin));
  const pendingReservationsCount = usePendingReservationsCount(shopId, Boolean(isUserReservationAdmin));

  const isSelected = (route: string) => {
    return isRouterPathMatchingRoute(pathname, route);
  };

  const isConfigurationSelected = () => {
    return (
      isSelected(`^${SHOP_ADMIN_PAGES_URLS['shop-details']}$`) ||
      isSelected(`^${SHOP_ADMIN_PAGES_URLS['edit-shop']}$`) ||
      isSelected(`^${SHOP_ADMIN_PAGES_URLS['categories-settings']}$`) ||
      isSelected(`^${SHOP_ADMIN_PAGES_URLS['promotion-codes']}$`) ||
      isSelected(`^${SHOP_ADMIN_PAGES_URLS['integrations']}$`)
    );
  };

  const isOrdersSelected = () => {
    return (
      isSelected(`^${SHOP_ADMIN_PAGES_URLS['order-administration']}$`) ||
      isSelected(`^${SHOP_ADMIN_PAGES_URLS['create-order']}$`)
    );
  };

  return (
    <div className={clsx(className, styles.root)} {...rest}>
      <MenuList dense>
        <Stack spacing={0.5}>
          {isUserShopAdmin && shopId && (
            <>
              <UnstyledLink href={`${SHOP_ADMIN_PAGES_URLS['shop-details']}?shp_id=${shopId}`}>
                {isCollapsed && (
                  <Tooltip enterDelay={1000} placement="right" title="Configuration">
                    <MenuItem className={styles.menuItem} selected={isConfigurationSelected()}>
                      <GearsSvg className={styles.icon} />
                    </MenuItem>
                  </Tooltip>
                )}
                {!isCollapsed && (
                  <MenuItem className={styles.menuItem} selected={isConfigurationSelected()} disableRipple>
                    <ListItemIcon>
                      <GearsSvg className={styles.icon} />
                    </ListItemIcon>
                    Configuration
                  </MenuItem>
                )}
              </UnstyledLink>
              {!isCollapsed && (
                <Stack pl={(!isCollapsed && 2) || 0.25} my={0.5} spacing={0.5}>
                  <UnstyledLink href={`${SHOP_ADMIN_PAGES_URLS['shop-details']}?shp_id=${shopId}`}>
                    <MenuItem
                      className={styles.menuItem}
                      selected={isSelected(`^${SHOP_ADMIN_PAGES_URLS['shop-details']}$`)}
                    >
                      <ListItemIcon>
                        <SettingsSvg className={styles.icon} />
                      </ListItemIcon>
                      Générale
                    </MenuItem>
                  </UnstyledLink>
                  <UnstyledLink href={`${SHOP_ADMIN_PAGES_URLS['promotion-codes']}?shp_id=${shopId}`}>
                    <MenuItem
                      className={styles.menuItem}
                      selected={isSelected(`^${SHOP_ADMIN_PAGES_URLS['promotion-codes']}$`)}
                    >
                      <ListItemIcon>
                        <TicketSvg className={styles.icon} />
                      </ListItemIcon>
                      Coupons
                    </MenuItem>
                  </UnstyledLink>
                  <UnstyledLink href={`${SHOP_ADMIN_PAGES_URLS['categories-settings']}?shp_id=${shopId}`}>
                    <MenuItem
                      className={styles.menuItem}
                      selected={isSelected(`^${SHOP_ADMIN_PAGES_URLS['categories-settings']}$`)}
                    >
                      <ListItemIcon>
                        <AppsSvg className={styles.icon} />
                      </ListItemIcon>
                      Catégories
                    </MenuItem>
                  </UnstyledLink>
                  <UnstyledLink href={`${SHOP_ADMIN_PAGES_URLS['edit-shop']}?shp_id=${shopId}`}>
                    <MenuItem
                      className={styles.menuItem}
                      selected={isSelected(`^${SHOP_ADMIN_PAGES_URLS['edit-shop']}$`)}
                    >
                      <ListItemIcon>
                        <SettingsSlidersSvg className={styles.icon} />
                      </ListItemIcon>
                      Avancée
                    </MenuItem>
                  </UnstyledLink>
                  <UnstyledLink href={`${SHOP_ADMIN_PAGES_URLS['integrations']}?shp_id=${shopId}`}>
                    <MenuItem
                      className={styles.menuItem}
                      selected={isSelected(`^${SHOP_ADMIN_PAGES_URLS['integrations']}$`)}
                    >
                      <ListItemIcon>
                        <Icon name="puzzle-piece-integration" className={styles.icon} />
                      </ListItemIcon>
                      Intégrations IA
                    </MenuItem>
                  </UnstyledLink>
                </Stack>
              )}
              {isUserOrderAdmin && (
                <UnstyledLink href={`${SHOP_ADMIN_PAGES_URLS['order-administration']}?shp_id=${shopId}`}>
                  {isCollapsed && (
                    <Tooltip enterDelay={1000} placement="right" title="Commandes">
                      <MenuItem className={styles.menuItem} selected={isOrdersSelected()}>
                        <PendingCountBadge count={pendingOrdersCount}>
                          <BarsSortSvg className={styles.icon} />
                        </PendingCountBadge>
                      </MenuItem>
                    </Tooltip>
                  )}
                  {!isCollapsed && (
                    <MenuItem className={styles.menuItem} selected={isOrdersSelected()}>
                      <ListItemIcon>
                        <BarsSortSvg className={styles.icon} />
                      </ListItemIcon>
                      Commandes
                      <PendingCountBadge count={pendingOrdersCount} />
                    </MenuItem>
                  )}
                </UnstyledLink>
              )}
              {isUserReservationAdmin && (
                <UnstyledLink href={`${SHOP_ADMIN_PAGES_URLS['reservations']}?shp_id=${shopId}`}>
                  {isCollapsed && (
                    <Tooltip enterDelay={1000} placement="right" title="Réservations">
                      <MenuItem
                        className={styles.menuItem}
                        selected={isSelected(`^${SHOP_ADMIN_PAGES_URLS['reservations']}$`)}
                      >
                        <PendingCountBadge count={pendingReservationsCount}>
                          <CalendarCheckSvg className={styles.icon} />
                        </PendingCountBadge>
                      </MenuItem>
                    </Tooltip>
                  )}
                  {!isCollapsed && (
                    <MenuItem
                      className={styles.menuItem}
                      selected={isSelected(`^${SHOP_ADMIN_PAGES_URLS['reservations']}$`)}
                    >
                      <ListItemIcon>
                        <CalendarCheckSvg className={styles.icon} />
                      </ListItemIcon>
                      Réservations
                      <PendingCountBadge count={pendingReservationsCount} />
                    </MenuItem>
                  )}
                </UnstyledLink>
              )}
              <UnstyledLink href={`${SHOP_ADMIN_PAGES_URLS['order-statistics']}?shp_id=${shopId}`}>
                {isCollapsed && (
                  <Tooltip enterDelay={1000} placement="right" title="Chiffres">
                    <MenuItem
                      className={styles.menuItem}
                      selected={isSelected(`^${SHOP_ADMIN_PAGES_URLS['order-statistics']}$`)}
                    >
                      <StatsSvg className={styles.icon} />
                    </MenuItem>
                  </Tooltip>
                )}
                {!isCollapsed && (
                  <MenuItem
                    className={styles.menuItem}
                    selected={isSelected(`^${SHOP_ADMIN_PAGES_URLS['order-statistics']}$`)}
                  >
                    <ListItemIcon>
                      <StatsSvg className={styles.icon} />
                    </ListItemIcon>
                    Chiffres
                  </MenuItem>
                )}
              </UnstyledLink>
              <UnstyledLink href={`${SHOP_ADMIN_PAGES_URLS['order-billing']}?shp_id=${shopId}`}>
                {isCollapsed && (
                  <Tooltip enterDelay={1000} placement="right" title="Facturation">
                    <MenuItem
                      className={styles.menuItem}
                      selected={isSelected(`^${SHOP_ADMIN_PAGES_URLS['order-billing']}$`)}
                    >
                      <FileInvoiceDollarSvg className={styles.icon} />
                    </MenuItem>
                  </Tooltip>
                )}
                {!isCollapsed && (
                  <MenuItem
                    className={styles.menuItem}
                    selected={isSelected(`^${SHOP_ADMIN_PAGES_URLS['order-billing']}$`)}
                  >
                    <ListItemIcon>
                      <FileInvoiceDollarSvg className={styles.icon} />
                    </ListItemIcon>
                    Facturation
                  </MenuItem>
                )}
              </UnstyledLink>
              <UnstyledLink href={`${SHOP_ADMIN_PAGES_URLS['users']}?shp_id=${shopId}`}>
                {isCollapsed && (
                  <Tooltip enterDelay={1000} placement="right" title="Clients">
                    <MenuItem className={styles.menuItem} selected={isSelected(`^${SHOP_ADMIN_PAGES_URLS['users']}$`)}>
                      <TargetAudienceSvg className={styles.icon} />
                    </MenuItem>
                  </Tooltip>
                )}
                {!isCollapsed && (
                  <MenuItem className={styles.menuItem} selected={isSelected(`^${SHOP_ADMIN_PAGES_URLS['users']}$`)}>
                    <ListItemIcon>
                      <TargetAudienceSvg className={styles.icon} />
                    </ListItemIcon>
                    Clients
                  </MenuItem>
                )}
              </UnstyledLink>
            </>
          )}
          {shop != null && (
            <UnstyledLink href={`${shop.websiteUrl}`} target="_blank">
              {isCollapsed && (
                <Tooltip enterDelay={1000} placement="right" title="Site web">
                  <MenuItem className={styles.menuItem} selected={false}>
                    <BrowserSvg className={styles.icon} />
                  </MenuItem>
                </Tooltip>
              )}
              {!isCollapsed && (
                <MenuItem className={styles.menuItem} selected={false}>
                  <ListItemIcon>
                    <BrowserSvg className={styles.icon} />
                  </ListItemIcon>
                  Site web
                  <Stack component="span" ml={0.5}>
                    <Icon name="expand" size="small" />
                  </Stack>
                </MenuItem>
              )}
            </UnstyledLink>
          )}
        </Stack>
      </MenuList>
    </div>
  );
};

export default NavMenu;
